import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Lightbulb, Plus, Trash2, Edit3, X, ChevronDown, ChevronRight,
  Target, Loader2, Clock, CheckCircle2, Link2, Save,
  Filter, Search, Shield, BookOpen, Factory, Wrench,
  Globe, FlaskConical, Warehouse, HeartPulse, Users,
  FileText, AlertTriangle, Zap
} from "lucide-react";

const STATUS_OPTIONS = ["draft", "active", "in-progress", "completed", "cancelled"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

const statusColors = {
  draft: "text-slate-400 bg-slate-400/10 border-slate-400/30",
  active: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30",
  "in-progress": "text-amber-400 bg-amber-400/10 border-amber-400/30",
  completed: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  cancelled: "text-red-400 bg-red-400/10 border-red-400/30",
};

const priorityColors = {
  low: "text-slate-400", medium: "text-cyan-400", high: "text-amber-400", critical: "text-red-400",
};

const categoryIcons = {
  quality_governance: Shield,
  supplier_sourcing: Users,
  shop_floor_ops: Factory,
  lifecycle_change: Zap,
  regulatory_lab_testing: FlaskConical,
  facilities_support: Warehouse,
  post_market_service: HeartPulse,
};

const categoryColors = {
  quality_governance: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30",
  supplier_sourcing: "text-amber-400 bg-amber-400/10 border-amber-400/30",
  shop_floor_ops: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  lifecycle_change: "text-violet-400 bg-violet-400/10 border-violet-400/30",
  regulatory_lab_testing: "text-rose-400 bg-rose-400/10 border-rose-400/30",
  facilities_support: "text-blue-400 bg-blue-400/10 border-blue-400/30",
  post_market_service: "text-pink-400 bg-pink-400/10 border-pink-400/30",
};

const ImprovementPage = () => {
  const { t } = useTranslation(['platform', 'common']);
  const [registry, setRegistry] = useState(null);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [expandedCats, setExpandedCats] = useState([]);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState("plans");
  const [selectedPlanType, setSelectedPlanType] = useState(null);

  const emptyForm = {
    plan_type: "", title: "", description: "", priority: "medium", status: "draft",
    sector: "", client_id: "", input_references: [], objectives: [], goals_kpis: [],
    acceptance_criteria: "", standard_refs: [], template_sections: {},
    actions: [], target_date: "", owner: "", notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [refInput, setRefInput] = useState("");
  const [objInput, setObjInput] = useState("");
  const [actionInput, setActionInput] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.set("category", filterCategory);
      if (filterStatus) params.set("status", filterStatus);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [regRes, plansRes, statsRes] = await Promise.all([
        fetch(`${API}/improvement/registry`, { credentials: "include" }),
        fetch(`${API}/improvement/plans${qs ? `${qs}&` : '?'}limit=100`, { credentials: "include" }),
        fetch(`${API}/improvement/stats`, { credentials: "include" }),
      ]);
      if (regRes.ok) setRegistry(await regRes.json());
      if (plansRes.ok) { const d = await plansRes.json(); setPlans(d.plans || []); }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterCategory, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => { setForm(emptyForm); setRefInput(""); setObjInput(""); setActionInput(""); };

  const startCreateFromType = (planType, category) => {
    const templateSections = {};
    (registry?.standard_template_sections || []).forEach(s => { templateSections[s.id] = ""; });
    setForm({
      ...emptyForm,
      plan_type: planType.id,
      title: `New ${planType.name}`,
      objectives: [...(planType.objectives || [])],
      goals_kpis: [...(planType.goals_kpis || [])],
      acceptance_criteria: planType.thresholds || "",
      standard_refs: [...(planType.standard_refs || [])],
      template_sections: templateSections,
    });
    setSelectedPlanType(planType);
    setShowCreate(true);
    setEditing(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editing ? `${API}/improvement/plans/${editing}` : `${API}/improvement/plans`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) { setShowCreate(false); setEditing(null); resetForm(); fetchAll(); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const startEdit = (plan) => {
    setForm({ ...plan });
    setEditing(plan.plan_id);
    setShowCreate(true);
    setSelectedPlanType(null);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm(t('platform:improvement.deletePlanConfirm'))) return;
    try { await fetch(`${API}/improvement/plans/${planId}`, { method: "DELETE", credentials: "include" }); fetchAll(); } catch (e) { console.error(e); }
  };

  const addListItem = (field, input, setInput) => {
    if (input.trim()) { setForm(p => ({ ...p, [field]: [...(p[field] || []), input.trim()] })); setInput(""); }
  };
  const removeListItem = (field, idx) => { setForm(p => ({ ...p, [field]: p[field].filter((_, i) => i !== idx) })); };

  const toggleCat = (catId) => setExpandedCats(prev => prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]);

  const filteredPlans = plans.filter(p => {
    if (searchQuery && !p.title.toLowerCase().includes(searchQuery.toLowerCase()) && !p.plan_id.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const categories = registry?.categories || [];
  const industryLabels = registry?.industry_labels || {};
  const allIndustries = [...new Set(categories.flatMap(c => c.plans.flatMap(p => p.industries || [])))];
  const templateSections = registry?.standard_template_sections || [];
  const regions = registry?.regulatory_regions || [];

  if (loading) {
    return <QMSLayout title={t('platform:improvement.title')}><div className="flex items-center justify-center h-64" data-testid="improvement-loading"><Loader2 className="w-8 h-8 animate-spin text-[#C9A84C]" /></div></QMSLayout>;
  }

  return (
    <QMSLayout title={t('platform:improvement.title')}>
      <div className="space-y-6" data-testid="improvement-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Lightbulb className="w-6 h-6 text-[#C9A84C]" /> {t('platform:improvement.pageTitle')}</h1>
            <p className="text-sm text-[#8B95A5] mt-1">{categories.reduce((sum, c) => sum + c.plans.length, 0)} {t('platform:improvement.planTypes')} — {categories.length} categories</p>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-1 bg-[#181B23] p-1 rounded-xl border border-[rgba(255,255,255,0.05)] w-fit">
          {[
            { id: "plans", label: t('platform:improvement.activePlans'), icon: FileText },
            { id: "catalog", label: t('platform:improvement.planCatalog'), icon: BookOpen },
            { id: "regulatory", label: t('platform:improvement.regulatoryMap'), icon: Globe },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeView === tab.id ? "bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30" : "text-[#8B95A5] hover:text-white border border-transparent"}`}
              data-testid={`view-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
              {tab.id === "plans" && plans.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-[9px] rounded-full bg-[#C9A84C]/20 text-[#C9A84C]">{plans.length}</span>}
            </button>
          ))}
        </div>

        {/* ===== ACTIVE PLANS VIEW ===== */}
        {activeView === "plans" && (
          <div className="space-y-4">
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="improvement-stats">
                <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-3 text-center">
                  <p className="text-white font-bold text-xl">{stats.total}</p>
                  <p className="text-[#8B95A5] text-[10px]">{t('platform:improvement.totalPlans')}</p>
                </div>
                {[
                  { key: "draft", label: t('common:status.draft') },
                  { key: "active", label: t('common:status.active') },
                  { key: "in-progress", label: t('common:status.inProgress') },
                  { key: "completed", label: t('common:status.completed') }
                ].map(item => (
                  <div key={item.key} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-3 text-center">
                    <p className="text-white font-bold text-xl">{stats.by_status?.[item.key] || 0}</p>
                    <p className="text-[#8B95A5] text-[10px] capitalize">{item.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search className="w-4 h-4 text-[#8B95A5] absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t('platform:improvement.searchPlans')}
                  className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-[#8B95A5] focus:border-[#C9A84C] focus:outline-none" data-testid="plan-search" />
              </div>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="filter-category">
                <option value="">{t('platform:improvement.allCategories')}</option>
                {categories.map(c => (<option key={c.id} value={c.id}>{c.label}</option>))}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="filter-status">
                <option value="">{t('platform:improvement.allStatuses')}</option>
                {STATUS_OPTIONS.map(s => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>

            {/* Plan Cards */}
            {filteredPlans.length === 0 ? (
              <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-12 text-center">
                <Lightbulb className="w-10 h-10 text-[#8B95A5] opacity-30 mx-auto mb-3" />
                <p className="text-[#8B95A5] text-sm">{t('platform:improvement.noPlans')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPlans.map(plan => {
                  const isExpanded = expandedPlan === plan.plan_id;
                  const CatIcon = categoryIcons[plan.category_id] || Shield;
                  const catColor = categoryColors[plan.category_id] || categoryColors.quality_governance;
                  return (
                    <div key={plan.plan_id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl overflow-hidden" data-testid={`plan-card-${plan.plan_id}`}>
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${catColor}`}>
                            <CatIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="text-xs font-mono text-[#C9A84C]">{plan.plan_id}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusColors[plan.status] || statusColors.draft}`}>{plan.status}</span>
                              <span className={`text-[10px] ${priorityColors[plan.priority]}`}>{plan.priority}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${catColor}`}>{plan.category_label}</span>
                            </div>
                            <p className="text-sm text-white truncate">{plan.title}</p>
                            <p className="text-[10px] text-[#8B95A5]">{plan.plan_type_label} {plan.sector && `· ${plan.sector}`} {plan.client_id && `· ${plan.client_id}`}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          {(plan.input_references?.length || 0) > 0 && <span className="flex items-center gap-0.5 text-[10px] text-[#C9A84C]"><Link2 className="w-3 h-3" />{plan.input_references.length}</span>}
                          <button onClick={() => startEdit(plan)} className="p-1.5 text-[#8B95A5] hover:text-[#C9A84C]" data-testid={`edit-plan-${plan.plan_id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setExpandedPlan(isExpanded ? null : plan.plan_id)} className="p-1.5 text-[#8B95A5] hover:text-white" data-testid={`expand-plan-${plan.plan_id}`}>
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDelete(plan.plan_id)} className="p-1.5 text-[#8B95A5] hover:text-red-400" data-testid={`delete-plan-${plan.plan_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="border-t border-[rgba(255,255,255,0.05)] p-4 bg-[#111318]/50 space-y-3">
                          {plan.description && <p className="text-sm text-[#8B95A5]">{plan.description}</p>}
                          {plan.input_references?.length > 0 && (
                            <div><span className="text-[10px] text-[#8B95A5] uppercase block mb-1">{t('platform:improvement.inputProcessRefs')}</span>
                              <div className="flex flex-wrap gap-1.5">{plan.input_references.map((ref, i) => (<span key={i} className="px-2 py-0.5 text-xs rounded bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/20 font-mono">{ref}</span>))}</div>
                            </div>
                          )}
                          {plan.objectives?.length > 0 && (
                            <div><span className="text-[10px] text-[#8B95A5] uppercase block mb-1">{t('platform:improvement.objectives')}</span>
                              <ul className="space-y-1">{plan.objectives.map((o, i) => (<li key={i} className="text-xs text-white flex items-start gap-2"><Target className="w-3 h-3 text-[#C9A84C] mt-0.5 shrink-0" />{o}</li>))}</ul>
                            </div>
                          )}
                          {plan.goals_kpis?.length > 0 && (
                            <div><span className="text-[10px] text-[#8B95A5] uppercase block mb-1">{t('platform:improvement.goalsKpis')}</span>
                              <div className="flex flex-wrap gap-1.5">{plan.goals_kpis.map((g, i) => (<span key={i} className="px-2 py-0.5 text-xs rounded bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">{g}</span>))}</div>
                            </div>
                          )}
                          {plan.acceptance_criteria && (
                            <div><span className="text-[10px] text-[#8B95A5] uppercase block mb-1">{t('platform:improvement.acceptanceCriteria')}</span>
                              <p className="text-xs text-white bg-[#181B23] p-2 rounded border border-[rgba(255,255,255,0.05)]">{plan.acceptance_criteria}</p>
                            </div>
                          )}
                          {plan.standard_refs?.length > 0 && (
                            <div><span className="text-[10px] text-[#8B95A5] uppercase block mb-1">{t('platform:improvement.standardRefs')}</span>
                              <div className="flex flex-wrap gap-1.5">{plan.standard_refs.map((r, i) => (<span key={i} className="px-2 py-0.5 text-[10px] rounded bg-slate-700/50 text-slate-300 border border-slate-600">{r}</span>))}</div>
                            </div>
                          )}
                          {plan.actions?.length > 0 && (
                            <div><span className="text-[10px] text-[#8B95A5] uppercase block mb-1">{t('platform:improvement.actions')}</span>
                              <ul className="space-y-1">{plan.actions.map((a, i) => (<li key={i} className="text-xs text-white flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />{a}</li>))}</ul>
                            </div>
                          )}
                          <div className="flex items-center gap-4 text-[10px] text-[#8B95A5]">
                            {plan.target_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('platform:improvement.target')}: {plan.target_date}</span>}
                            {plan.owner && <span>{t('platform:improvement.owner')}: {plan.owner}</span>}
                            <span>{t('platform:improvement.created')}: {new Date(plan.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ===== PLAN CATALOG VIEW ===== */}
        {activeView === "catalog" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)}
                className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="catalog-industry-filter">
                <option value="">{t('platform:improvement.allIndustries')}</option>
                {allIndustries.map(ind => (<option key={ind} value={ind}>{industryLabels[ind] || ind}</option>))}
              </select>
              <span className="text-xs text-[#8B95A5]">
                {categories.reduce((sum, c) => sum + c.plans.filter(p => !filterIndustry || p.industries?.includes(filterIndustry)).length, 0)} plan types
              </span>
            </div>

            {categories.map(cat => {
              const CatIcon = categoryIcons[cat.id] || Shield;
              const catColor = categoryColors[cat.id] || categoryColors.quality_governance;
              const isOpen = expandedCats.includes(cat.id);
              const filteredCatPlans = cat.plans.filter(p => !filterIndustry || p.industries?.includes(filterIndustry));
              if (filteredCatPlans.length === 0) return null;
              return (
                <div key={cat.id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl overflow-hidden" data-testid={`catalog-cat-${cat.id}`}>
                  <button onClick={() => toggleCat(cat.id)} className="w-full p-4 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${catColor}`}><CatIcon className="w-4 h-4" /></div>
                      <div className="text-left">
                        <h3 className="text-sm font-semibold text-white">{cat.label}</h3>
                        <p className="text-[10px] text-[#8B95A5]">{cat.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#8B95A5]">{filteredCatPlans.length} types</span>
                      {isOpen ? <ChevronDown className="w-4 h-4 text-[#8B95A5]" /> : <ChevronRight className="w-4 h-4 text-[#8B95A5]" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-[rgba(255,255,255,0.05)] divide-y divide-[rgba(255,255,255,0.03)]">
                      {filteredCatPlans.map(planType => (
                        <div key={planType.id} className="p-4 hover:bg-[rgba(255,255,255,0.02)] transition-colors" data-testid={`catalog-type-${planType.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm text-white font-medium">{planType.name}</h4>
                              <p className="text-xs text-[#8B95A5] mt-0.5 leading-relaxed">{planType.description}</p>
                            </div>
                            <button onClick={() => startCreateFromType(planType, cat)}
                              className="ml-3 shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/30 hover:bg-[#C9A84C]/20 transition-colors"
                              data-testid={`create-from-${planType.id}`}
                            ><Plus className="w-3 h-3" /> Create</button>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {(planType.industries || []).map(ind => (<span key={ind} className="text-[9px] px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.04)] text-[#8B95A5] border border-[rgba(255,255,255,0.06)]">{industryLabels[ind] || ind}</span>))}
                          </div>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {(planType.standard_refs || []).map((ref, i) => (<span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700/30 text-slate-400 border border-slate-600/50">{ref}</span>))}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-[10px]">
                            <div><span className="text-[#8B95A5]">Thresholds:</span> <span className="text-white">{planType.thresholds}</span></div>
                            <div><span className="text-[#8B95A5]">Effectiveness:</span> <span className="text-white">{planType.effectiveness_check}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ===== REGULATORY MAP VIEW ===== */}
        {activeView === "regulatory" && (
          <div className="space-y-4">
            <p className="text-sm text-[#8B95A5]">{t('platform:improvement.regulatorySubtitle')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {regions.map(region => (
                <div key={region.id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid={`region-card-${region.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-[#C9A84C]" />
                    <h3 className="text-sm font-semibold text-white">{region.id.toUpperCase()}</h3>
                    <span className="text-xs text-[#8B95A5]">{region.authority}</span>
                  </div>
                  <div className="mb-3">
                    <span className="text-[10px] text-[#8B95A5] uppercase block mb-1">{t('platform:improvement.frameworks')}</span>
                    <div className="flex flex-wrap gap-1">{region.frameworks.map((fw, i) => (<span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/30 text-slate-300 border border-slate-600/50">{fw}</span>))}</div>
                  </div>
                  <div>
                    <span className="text-[10px] text-[#8B95A5] uppercase block mb-1">Submission Checklist</span>
                    <p className="text-xs text-white leading-relaxed">{region.checklist}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CREATE / EDIT MODAL ===== */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" data-testid="plan-modal">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.1)] rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-5 border-b border-[rgba(255,255,255,0.07)] flex items-center justify-between sticky top-0 bg-[#1F222D] z-10">
                <div>
                  <h2 className="text-lg font-semibold text-white">{editing ? t('platform:improvement.editPlan') : t('platform:improvement.createPlan')}</h2>
                  {selectedPlanType && <p className="text-xs text-[#C9A84C] mt-0.5">{selectedPlanType.name}</p>}
                </div>
                <button onClick={() => { setShowCreate(false); setEditing(null); resetForm(); }} className="p-1 text-[#8B95A5] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                {/* Plan Type (if not pre-selected) */}
                {!selectedPlanType && !editing && (
                  <div>
                    <label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.planType')}</label>
                    <select value={form.plan_type} onChange={e => setForm({ ...form, plan_type: e.target.value })}
                      className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-plan-type">
                      <option value="">{t('platform:improvement.selectType')}</option>
                      {categories.map(cat => (<optgroup key={cat.id} label={cat.label}>{cat.plans.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}</optgroup>))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.labelTitle')}</label>
                  <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-title" />
                </div>
                <div>
                  <label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.labelDescription')}</label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none resize-none" data-testid="modal-desc" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.priority')}</label>
                    <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-priority">{PRIORITY_OPTIONS.map(p => (<option key={p} value={p}>{p}</option>))}</select></div>
                  <div><label className="text-xs text-[#8B95A5] block mb-1">Status</label>
                    <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-status">{STATUS_OPTIONS.map(s => (<option key={s} value={s}>{s}</option>))}</select></div>
                  <div><label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.sector')}</label>
                    <select value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-sector">
                      <option value="">{t('platform:improvement.selectSector')}</option>{allIndustries.map(s => (<option key={s} value={s}>{industryLabels[s] || s}</option>))}</select></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.clientId')}</label><input value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-client" /></div>
                  <div><label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.targetDate')}</label><input type="date" value={form.target_date} onChange={e => setForm({ ...form, target_date: e.target.value })} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-date" /></div>
                  <div><label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.ownerLabel')}</label><input value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none" data-testid="modal-owner" /></div>
                </div>

                {/* Input References */}
                <ListField label={t('platform:improvement.inputProcessRefsLabel')} items={form.input_references} input={refInput} setInput={setRefInput} onAdd={() => addListItem("input_references", refInput, setRefInput)} onRemove={(i) => removeListItem("input_references", i)} icon={Link2} color="#C9A84C" testId="ref" />

                {/* Objectives */}
                <ListField label={t('platform:improvement.objectivesLabel')} items={form.objectives} input={objInput} setInput={setObjInput} onAdd={() => addListItem("objectives", objInput, setObjInput)} onRemove={(i) => removeListItem("objectives", i)} icon={Target} color="#C9A84C" testId="obj" />

                {/* Actions */}
                <ListField label={t('platform:improvement.actionsLabel')} items={form.actions} input={actionInput} setInput={setActionInput} onAdd={() => addListItem("actions", actionInput, setActionInput)} onRemove={(i) => removeListItem("actions", i)} icon={CheckCircle2} color="#4ADE80" testId="action" />

                {/* Acceptance Criteria */}
                <div>
                  <label className="text-xs text-[#8B95A5] block mb-1">Acceptance Criteria / Thresholds</label>
                  <textarea value={form.acceptance_criteria} onChange={e => setForm({ ...form, acceptance_criteria: e.target.value })} rows={2} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none resize-none" data-testid="modal-criteria" />
                </div>

                {/* Standard Template Sections */}
                {templateSections.length > 0 && (
                  <div>
                    <label className="text-xs text-[#8B95A5] block mb-2 flex items-center gap-1"><BookOpen className="w-3 h-3" /> {t('platform:improvement.standardPlanSections')}</label>
                    <div className="space-y-2">
                      {templateSections.map(sec => (
                        <div key={sec.id}>
                          <label className="text-[10px] text-white block mb-0.5">{sec.label} <span className="text-[#8B95A5]">— {sec.description}</span></label>
                          <textarea value={form.template_sections?.[sec.id] || ""} onChange={e => setForm(prev => ({ ...prev, template_sections: { ...prev.template_sections, [sec.id]: e.target.value } }))} rows={2}
                            className="w-full bg-[#111318] border border-[rgba(255,255,255,0.05)] rounded-lg px-3 py-2 text-xs text-white focus:border-[#C9A84C] focus:outline-none resize-none" data-testid={`section-${sec.id}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs text-[#8B95A5] block mb-1">{t('platform:improvement.notesLabel')}</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C9A84C] focus:outline-none resize-none" data-testid="modal-notes" />
                </div>
              </div>
              <div className="p-5 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3 sticky bottom-0 bg-[#1F222D]">
                <button onClick={() => { setShowCreate(false); setEditing(null); resetForm(); }} className="px-4 py-2 rounded-lg text-sm text-[#8B95A5] hover:text-white">{t('common:actions.cancel')}</button>
                <button onClick={handleSave} disabled={saving || !form.title?.trim() || (!form.plan_type && !editing)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#D4B85E] transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="modal-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editing ? t('platform:improvement.updatePlan') : t('platform:improvement.create')} {t('platform:improvement.plan')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

const ListField = ({ label, items, input, setInput, onAdd, onRemove, icon: Icon, color, testId }) => (
  <div>
    <label className="text-xs text-[#8B95A5] block mb-1">{label}</label>
    <div className="flex gap-2">
      <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), onAdd())}
        className="flex-1 bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-3 py-2 text-sm text-white placeholder-[#8B95A5] focus:border-[#C9A84C] focus:outline-none" data-testid={`modal-${testId}-input`} />
      <button onClick={onAdd} className="px-3 py-2 rounded-lg bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/30 text-sm hover:bg-[#C9A84C]/20">Add</button>
    </div>
    {items?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {items.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border font-mono" style={{ background: `${color}15`, color, borderColor: `${color}30` }}>
            <Icon className="w-3 h-3" />{item}
            <button onClick={() => onRemove(i)} className="hover:text-red-400 ml-0.5"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    )}
  </div>
);

export default ImprovementPage;
